import { Menu, Navigation, LogIn, UserCircle, Shield, BookOpen, Calendar, LogOut, Briefcase, Palette } from 'lucide-react';
import { ServiceConfig } from '../utils/serviceKeys';
import { useEffect, useState } from 'react';
import { useRoleContext } from '../context/RoleContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout as graphqlLogout } from '../services/graphql/authService';
import type { User } from '../services/graphql/authService';
import ThemeToggleButton from './ThemeToggleButton';
import { ThemeEditorModal } from './ThemeEditor/ThemeEditorModal';
import { ColorScheme } from './ColorSchemeEditor/types';
import { saveColorScheme, applyColorScheme } from '../services/themeService';
import { useTheme } from '../context/ThemeContext';
import { trackUserInteraction, trackAuthEvent } from '../services/analytics';

interface HeaderProps {
  currentUser: User | null;
  serviceConfig: ServiceConfig;
  onShowBookingForm: () => void;
  onShowMyBookings: () => void;
}

export function Header({ currentUser, serviceConfig, onShowBookingForm, onShowMyBookings }: HeaderProps) {
  const serviceName = serviceConfig.serviceName || 'RideQuest';
  const region = serviceConfig.attributes?.region;
  // Use role context for admin access
  const { userProfile, loading: roleLoading } = useRoleContext ? useRoleContext() : { userProfile: null, loading: false };
  // Use state for admin access to allow async updates
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false); // Added state for superadmin
  const [isServiceProviderAdmin, setIsServiceProviderAdmin] = useState(false); // Added state for serviceprovideradmin
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [themeEditorOpen, setThemeEditorOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Check if user has admin access (either superadmin or baseservice)
  useEffect(() => {
    // Wait for userProfile to load
    if (roleLoading) return;
    if (!currentUser) {
      setHasAdminAccess(false);
      setIsSuperAdmin(false);
      setIsServiceProviderAdmin(false);
      return;
    }

    // Check role from user labels (GraphQL format) or userProfile
    const labels = currentUser.labels || [];
    let role = userProfile?.role;

    // Normalize labels for comparison
    const normalizedLabels = labels.map(l => l.toLowerCase());
    const normalizedRole = typeof role === 'string' ? role.toLowerCase() : '';

    let isAdmin = false;
    let superAdmin = false;
    let serviceProviderAdmin = false;

    // Check labels first (GraphQL auth), then fallback to userProfile role
    if (normalizedLabels.includes('super_admin') || normalizedRole === 'superadmin') {
      isAdmin = true;
      superAdmin = true;
    } else if (normalizedLabels.includes('service_provider_admin') || normalizedRole === 'serviceprovideradmin') {
      isAdmin = true;
      serviceProviderAdmin = true;
    }

    setHasAdminAccess(isAdmin);
    setIsSuperAdmin(superAdmin);
    setIsServiceProviderAdmin(serviceProviderAdmin);
  }, [currentUser, userProfile, roleLoading]);

  /**
   * Handles user logout using GraphQL API.
   */
  const handleLogout = async () => {
    try {
      // Track logout attempt
      trackAuthEvent('Logout Attempt', 'email', currentUser?.$id);

      await graphqlLogout();

      // Track successful logout
      trackAuthEvent('Logout Success', 'email');

      // Reload and redirect to service default page after logout
      window.location.href = `/${serviceConfig.serviceId}`;
    } catch (error) {
      // Track logout failure
      trackAuthEvent('Logout Failed', 'email', currentUser?.$id);

      console.error('Logout failed:', error);
    }
  };
  /**
   * Handles toggling the theme editor modal
   */
  const handleToggleThemeEditor = () => {
    setThemeEditorOpen(!themeEditorOpen);
    trackUserInteraction('UI:Theme Editor Toggle', { userId: currentUser?.email || 'anonymous' });
  };

  /**
   * Handles closing the theme editor modal
   */
  const handleCloseThemeEditor = () => {
    setThemeEditorOpen(false);
  };

  /**
   * Handles saving a color scheme from the theme editor
   */
  const handleSaveColorScheme = async (scheme: ColorScheme) => {
    try {
      const result = await saveColorScheme(scheme);
      
      if (result.success) {
        // Apply the scheme to the current theme mode
        const applyResult = applyColorScheme(scheme, theme);
        
        if (applyResult.success) {
          trackUserInteraction('UI:Theme Editor Save Success', { 
            userId: currentUser?.email || 'anonymous',
            schemeName: scheme.name 
          });
        }
      }
    } catch (error) {
      console.error('Failed to save color scheme:', error);
    }
  };

  return (
    <header className="bg-background-paper shadow-sm text-foreground" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Navigation className="h-8 w-8 text-primary" />          <div>
            <Link to={`/${serviceConfig.serviceId}`} className="text-xl font-bold hover:underline focus:underline text-foreground" data-testid="header-logo-link">
              {serviceName}
            </Link>

            {region && (
              <span className="text-sm text-muted-foreground ml-2">({region})</span>
            )}
                        <div className="text-xs text-green-500">
              powered by mythos365.com
            </div>
          </div>
        </div>
        
        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center space-x-4"> {/* Adjusted space */}
          {currentUser ? (
            <>
              <div className="flex items-center" title={currentUser.email || 'User'} data-testid="header-user-info">                <UserCircle className="h-6 w-6 text-foreground" />                <span className="ml-2 text-sm text-foreground" data-testid="header-user-name">
                  {userProfile?.full_name || currentUser.email?.split('@')[0]}
                  {isSuperAdmin && (
                    <span className="ml-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-medium rounded-full dark:bg-purple-900 dark:text-purple-300" data-testid="header-superadmin-badge">
                      Superadmin
                    </span>
                  )}
                  {isServiceProviderAdmin && (
                    <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full dark:bg-blue-900 dark:text-blue-300" data-testid="header-service-provider-badge">
                      Service Provider Admin
                    </span>
                  )}
                </span>
              </div>

              {/* Book a Ride link */}
              <Link
                to={`/${serviceConfig.serviceId}`}
                className={`flex items-center text-sm transition-colors ${
                  location.pathname === `/${serviceConfig.serviceId}`
                    ? 'text-accent font-semibold'
                    : 'text-foreground hover:text-accent'
                }`}
                aria-label="Book a Ride"
                data-testid="header-nav-book-a-ride"
                onClick={() => {
                  // Assign object to a variable before passing to the function
                  const eventData = { userId: currentUser.email };
                  trackUserInteraction('UI:Book a Ride', eventData);
                }} // Track interaction
              >
                <Calendar className="h-4 w-4 mr-1" />
                Book a Ride
              </Link>
              {/* My Bookings link */}
              <Link
                to={`/${serviceConfig.serviceId}/bookings`}
                className={`flex items-center text-sm transition-colors ${
                  location.pathname.startsWith(`/${serviceConfig.serviceId}/bookings`)
                    ? 'text-accent font-semibold'
                    : 'text-foreground hover:text-accent'
                }`}
                aria-label="My Bookings"
                data-testid="header-nav-my-bookings"
                onClick={() => trackUserInteraction('UI:My Bookings', { userId: currentUser.email })} // Track interaction
              >
                <BookOpen className="h-4 w-4 mr-1" />
                My Bookings
              </Link>

              {/* Base Bookings link for ServiceProviderAdmin */}
              {isServiceProviderAdmin && (
                <Link
                  to={`/${serviceConfig.serviceId}/base-bookings`}
                  className={`flex items-center text-sm transition-colors ${
                    location.pathname.startsWith(`/${serviceConfig.serviceId}/base-bookings`)
                      ? 'text-accent font-semibold'
                      : 'text-foreground hover:text-accent'
                  }`}
                  aria-label="Base Bookings"
                  data-testid="header-nav-base-bookings"
                  onClick={() => trackUserInteraction('UI:Base Bookings', { userId: currentUser.email })}
                >
                  <Briefcase className="h-4 w-4 mr-1" /> {/* Assuming Briefcase icon, replace if needed */}
                  Base Bookings
                </Link>
              )}              {/* Admin Dashboard link - only for superadmin */}
              {isSuperAdmin && (
                <Link
                  to={`/${serviceConfig.serviceId}/system/admin`}
                  className={`flex items-center text-sm transition-colors ${
                    location.pathname.startsWith(`/${serviceConfig.serviceId}/system/admin`)
                      ? 'text-accent font-semibold'
                      : 'text-foreground hover:text-accent'
                  }`}
                  title="Access Admin Dashboard"
                  aria-label="Admin Dashboard"
                  data-testid="header-nav-admin-dashboard"
                  onClick={() => trackUserInteraction('UI:Admin Dashboard', { userId: currentUser.email })}
                >
                  <Shield className="h-4 w-4 mr-1" />
                  Admin
                </Link>
              )}

              {/* Theme Editor link - for superadmin users only */}
              {isSuperAdmin && (
                <button
                  onClick={handleToggleThemeEditor}
                  className="flex items-center text-sm text-foreground hover:text-accent transition-colors"
                  title="Toggle Theme Editor"
                  aria-label="Theme Editor"
                  data-testid="header-nav-theme-editor"
                >
                  <Palette className="h-4 w-4 mr-1" />
                  Theme Editor
                </button>
              )}

              <ThemeToggleButton /> {/* Added Theme Toggle Button */}<button
                onClick={handleLogout}
                className="flex items-center text-sm text-destructive hover:text-destructive/80 transition-colors"
                aria-label="Logout"
                type="button"
                data-testid="header-logout-button"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout
              </button>
            </>
          ) : (
            <>
              <ThemeToggleButton /> {/* Added Theme Toggle Button */}
              <button
                type="button"
                className="flex items-center p-2 hover:bg-secondary rounded-full transition-colors"
                title="Login/Signup"
                aria-label="Login or sign up"
                data-testid="header-login-signup-link"
                onClick={() => {
                  trackUserInteraction('UI:Login/Signup', { userId: '' });
                  window.dispatchEvent(new CustomEvent('showLoginPanel'));
                }}
              >
                <LogIn className="h-6 w-6 text-foreground" />
                <span className="ml-2 text-sm text-foreground">Login / Signup</span>
              </button>
            </>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center space-x-2">
          <ThemeToggleButton /> {/* Added Theme Toggle Button for mobile */}
          <button
            className="p-2 hover:bg-secondary rounded-full"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
            data-testid="header-mobile-menu-button"
          >
            <Menu className="h-6 w-6 text-foreground" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-paper border-t border-border py-2" data-testid="header-mobile-menu">
          {currentUser ? (
            <div className="space-y-2 px-4">
              <div className="py-2 border-b border-border">                <div className="flex items-center py-1" title={currentUser.email || "User"} data-testid="header-mobile-user-info">
                  <UserCircle className="h-5 w-5 text-foreground" />                  <span className="ml-2 text-sm text-foreground">
                    {userProfile?.full_name || currentUser.email}
                    {isSuperAdmin && (
                      <div className="mt-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-medium rounded-full w-fit dark:bg-purple-900 dark:text-purple-300" data-testid="header-mobile-superadmin-badge">
                        Superadmin
                      </div>
                    )}
                    {isServiceProviderAdmin && (
                      <div className="mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-fit dark:bg-blue-900 dark:text-blue-300" data-testid="header-mobile-service-provider-badge">
                        Service Provider Admin
                      </div>
                    )}
                  </span>
                </div>
              </div>
              {/* Book a Ride (Link, matches desktop) */}
              <Link
                to={`/${serviceConfig.serviceId}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  trackUserInteraction('UI:Book a Ride (Mobile)', { userId: currentUser.email });
                }}
                className={`flex items-center py-2 text-sm ${
                  location.pathname === `/${serviceConfig.serviceId}`
                    ? 'text-accent font-semibold'
                    : 'text-foreground'
                }`}
                aria-label="Book a Ride"
                data-testid="header-mobile-nav-book-a-ride"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Book a Ride
              </Link>
              {/* My Bookings (Link, matches desktop) */}
              <Link
                to={`/${serviceConfig.serviceId}/bookings`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  trackUserInteraction('UI:My Bookings (Mobile)', { userId: currentUser.email });
                }}
                className={`flex items-center py-2 text-sm ${
                  location.pathname.startsWith(`/${serviceConfig.serviceId}/bookings`)
                    ? 'text-accent font-semibold'
                    : 'text-foreground'
                }`}
                aria-label="My Bookings"
                data-testid="header-mobile-nav-my-bookings"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                My Bookings
              </Link>
              {/* Base Bookings for ServiceProviderAdmin */}
              {isServiceProviderAdmin && (
                <Link
                  to={`/${serviceConfig.serviceId}/base-bookings`}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    trackUserInteraction('UI:Base Bookings Mobile', { userId: currentUser.email });
                  }}
                  className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:bg-accent hover:text-accent-foreground"
                  aria-label="Base Bookings"
                  data-testid="header-mobile-nav-base-bookings"
                >
                  Base Bookings
                </Link>
              )}              {/* Admin Dashboard link (only for superadmin) */}
              {isSuperAdmin && (
                <Link
                  to={`/${serviceConfig.serviceId}/system/admin`}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    trackUserInteraction('UI:Admin Dashboard Mobile', { userId: currentUser.email });
                  }}
                  className="block px-3 py-2 rounded-md text-base font-medium text-foreground hover:bg-accent hover:text-accent-foreground"
                  aria-label="Admin Dashboard"
                  data-testid="header-mobile-nav-admin-dashboard"
                >
                  Admin Dashboard
                </Link>
              )}

              {/* Theme Editor button (for superadmin users only) */}
              {isSuperAdmin && (
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleToggleThemeEditor();
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-foreground hover:bg-accent hover:text-accent-foreground"
                  aria-label="Theme Editor"
                  data-testid="header-mobile-nav-theme-editor"
                >
                  <Palette className="h-4 w-4 mr-2 inline" />
                  Theme Editor
                </button>
              )}

              <div className="border-t border-border my-1"></div>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                  trackUserInteraction('UI:Logout (Mobile)', { userId: currentUser.email });
                }}
                className="block w-full text-left px-4 py-2 text-sm text-destructive hover:bg-secondary"
                type="button"
                data-testid="header-mobile-logout-button"
              >
                Logout
              </button>
            </div>
          ) : (
            <div className="px-4 py-2">
              <button
                type="button"
                className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-secondary"
                onClick={() => {
                  setMobileMenuOpen(false);
                  trackUserInteraction('UI:Login/Signup (Mobile)', { userId: '' });
                  window.dispatchEvent(new CustomEvent('showLoginPanel'));
                }}
                data-testid="header-mobile-login-signup-link"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Login / Signup
              </button>
              <div className="border-t border-border my-1"></div>
              <Link
                to={`/${serviceConfig.serviceId}`}
                className="block px-4 py-2 text-sm text-foreground hover:bg-secondary"
                onClick={() => {
                  setMobileMenuOpen(false);
                  trackUserInteraction('UI:Book a Ride (Mobile)', { userId: '' });
                }}
              >
                Book a Ride
              </Link>
              <Link
                to={`/${serviceConfig.serviceId}/bookings`}
                className="block px-4 py-2 text-sm text-foreground hover:bg-secondary"
                onClick={() => {
                  setMobileMenuOpen(false);
                  trackUserInteraction('UI:My Bookings (Mobile)', { userId: '' });
                }}
              >
                My Bookings
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Theme Editor Modal */}
      <ThemeEditorModal
        isOpen={themeEditorOpen}
        onClose={handleCloseThemeEditor}
        onSave={handleSaveColorScheme}
      />
    </header>
  );
}